import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Input, Select, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DownOutlined,
  LoadingOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import styled, { createGlobalStyle } from "styled-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import theme from "../../themes";
import {
  pageShellCss,
  pageTitleCss,
  searchFieldCss,
  tableScrollCss,
  wrapRowCss,
} from "../../styles/responsive";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as ArrowUpIcon } from "../../assets/icons/arrowUp.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrowDown.svg";
import { useAppDispatch, useAppSelector } from "../../store";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { getReportThunk } from "../../store/actions/report.action";
import {
  formatCount,
  getHandledByAiRate,
  getRecipientCount,
  getReplyRate,
  getRowChannels,
  rowHasEnabledChannel,
  toPct,
} from "./helpers";
import { outboundReportStartDate } from "./reportLimits";
import { useChannelSettings } from "../../hooks/useChannelSettings";

dayjs.extend(utc);

type ReportTab = "Inbound" | "Outbound";
const { Option } = Select;

const channelIcon = (key: string) => {
  if (key === "call") return <PhoneOutlined />;
  if (key === "sms") return <MessageOutlined />;
  if (key === "whatsapp") return <WhatsAppOutlined />;
  return <MailOutlined />;
};

const ReportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const restoredState = (location.state as any) || {};
  const [tab, setTab] = useState<ReportTab>(
    restoredState.tab === "Outbound" ? "Outbound" : "Inbound"
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sorter, setSorter] = useState<{
    field: "createdAt";
    order: "ascend" | "descend";
  }>({ field: "createdAt", order: "descend" });
  const pageSize = 8;
  const { loading: reportLoading } = useAppSelector((state) => state.report);
  const { isEnabled } = useChannelSettings();
  const [reports, setReports] = useState({
    data: [] as any[],
    pagination: { totalItems: 0, totalPages: 0 },
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, tab, sorter]);

  useEffect(() => {
    const fetchReports = async () => {
      const res = await dispatch(
        getReportThunk({
          page: currentPage,
          limit: pageSize,
          keyword: debouncedSearch,
          status: "",
          direction_filter: tab === "Inbound" ? "inbound" : "outbound",
          ...(tab === "Outbound"
            ? { start_date: outboundReportStartDate() }
            : {}),
          sort: {
            [sorter.field]: sorter.order === "descend" ? -1 : 1,
          },
        })
      ).unwrap();
      const { data, pagination } = res.data || { data: [], pagination: {} };
      setReports({
        data: (data || []).map((item: any, index: number) => ({
          ...item,
          key: item.key || item._id || item.id || `row-${index}`,
        })),
        pagination: pagination || { totalItems: 0, totalPages: 0 },
      });
    };
    fetchReports();
  }, [currentPage, debouncedSearch, tab, sorter, dispatch]);

  const listRows = useMemo(
    () => reports.data.filter((row) => rowHasEnabledChannel(row, isEnabled)),
    [reports.data, isEnabled]
  );

  const isInbound = tab === "Inbound";

  const openDetail = (row: any) => {
    const channels = getRowChannels(row);
    navigate(`/reports/${row._id}`, {
      state: {
        tab,
        isInbound,
        agentName: row.agent_name,
        campaignName: row.campaign_name,
        channels,
        channel: channels[0],
        recipients: getRecipientCount(row),
        handledByAi: toPct(getHandledByAiRate(row)),
        replyRate: getReplyRate(row),
        createdAt: row.createdAt,
      },
    });
  };

  const handleCreatedSort = () => {
    setSorter((prev) => ({
      field: "createdAt",
      order: prev.order === "descend" ? "ascend" : "descend",
    }));
  };

  const columns: ColumnsType<any> = [
    {
      title: isInbound ? t("reports.agentName") : t("reports.campaignName"),
      key: "name",
      render: (_: unknown, row: any) => (
        <NameLink type="button" onClick={() => openDetail(row)}>
          {isInbound ? row.agent_name : row.campaign_name}
        </NameLink>
      ),
    },
    {
      title: t("reports.v2.channel"),
      key: "channel",
      width: 120,
      render: (_: unknown, row: any) => (
        <ChannelCell>
          {getRowChannels(row)
            .filter((channel) => isEnabled(channel))
            .map((channel) => (
              <span key={channel}>{channelIcon(channel)}</span>
            ))}
        </ChannelCell>
      ),
    },
    {
      title: isInbound
        ? t("reports.v2.totalConversations")
        : t("reports.v2.recipients"),
      key: "recipients",
      width: 128,
      render: (_: unknown, row: any) => (
        <MetricCell>{formatCount(getRecipientCount(row))}</MetricCell>
      ),
    },
    {
      title: isInbound
        ? t("reports.v2.handledByAi")
        : t("reports.v2.replyRate"),
      key: "outcome",
      width: 140,
      render: (_: unknown, row: any) => {
        const replyRate = getReplyRate(row);
        const outcome = isInbound
          ? `${toPct(getHandledByAiRate(row))}%`
          : replyRate == null
          ? "—"
          : `${toPct(replyRate)}%`;
        return <MetricCell>{outcome}</MetricCell>;
      },
    },
    {
      title: (
        <span
          style={{
            cursor: "pointer",
            color: "#000",
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleCreatedSort();
          }}
        >
          {t("reports.createdTime")}{" "}
          {sorter.order === "ascend" ? (
            <ArrowUpIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
          ) : (
            <ArrowDownIcon style={{ marginLeft: 4, verticalAlign: "middle" }} />
          )}
        </span>
      ),
      key: "createdAt",
      width: 200,
      onHeaderCell: () => ({ style: { cursor: "pointer" } }),
      render: (_: unknown, row: any) => (
        <CreatedTime>
          {row.createdAt
            ? dayjs(row.createdAt).format("h:mm A, MMM D, YYYY")
            : "—"}
        </CreatedTime>
      ),
    },
  ];

  return (
    <Page>
      <ReportTypeDropdownStyles />
      <Header>
        <Title>{t("reports.title")}</Title>
      </Header>

      <SectionStack>
        <CardWrapper>
          <Toolbar>
            <TypeSelect
              value={tab}
              onChange={(value: unknown) => setTab(value as ReportTab)}
              suffixIcon={
                reportLoading ? <LoadingOutlined /> : <DownOutlined />
              }
              popupMatchSelectWidth
              popupClassName="report-type-dropdown"
              menuItemSelectedIcon={null}
              aria-label={t("reports.title")}
            >
              <Option value="Outbound">{t("reports.outboundCampaigns")}</Option>
              <Option value="Inbound">
                {t("reports.inboundConversations")}
              </Option>
            </TypeSelect>
            <SearchWrapper>
              <SearchIcon />
              <CustomSearch
                placeholder={
                  isInbound
                    ? t("reports.searchAgent")
                    : t("reports.searchCampaign")
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={() => setDebouncedSearch(search.trim())}
                allowClear
              />
            </SearchWrapper>
          </Toolbar>
          <StyledTable
            dataSource={listRows}
            columns={columns}
            loading={reportLoading}
            pagination={{
              current: currentPage,
              pageSize,
              total: reports.pagination.totalItems,
              position: ["bottomCenter"],
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            rowKey="key"
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("common.noData")}
                />
              ),
            }}
          />
        </CardWrapper>
        <KpiNote>
          {isInbound ? t("reports.v2.listNoteIn") : t("reports.v2.listNoteOut")}
        </KpiNote>
      </SectionStack>
    </Page>
  );
};

const Page = styled.div`
  background: ${theme.colors.grey3};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 32px;
  ${pageShellCss}
`;

const Header = styled.div`
  ${wrapRowCss}
  min-height: 48px;
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 600;
  font-size: 24px;
  line-height: 1;
  color: #000;
  ${pageTitleCss}
`;

const Toolbar = styled.div`
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TypeSelect = styled(Select)`
  && {
    width: 236px;
    max-width: 100%;
    height: 40px;
  }

  @media (max-width: 640px) {
    && {
      width: 100%;
    }
  }

  .ant-select-selector {
    box-sizing: border-box !important;
    height: 40px !important;
    min-height: 40px !important;
    border-radius: 8px !important;
    background: ${theme.colors.grey4} !important;
    border: 1px solid ${theme.colors.grey2} !important;
    box-shadow: none !important;
    display: flex !important;
    align-items: center !important;
    padding: 0 12px !important;
  }

  .ant-select-selector:focus,
  .ant-select-selector:active,
  &.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
    border-color: ${theme.colors.grey2} !important;
  }

  .ant-select-selection-wrap {
    height: 100%;
    display: flex;
    align-items: center;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    font-size: 14px;
    color: #111827 !important;
  }

  .ant-select-arrow {
    inset-inline-end: 12px;
    margin-top: 0;
    top: 50%;
    transform: translateY(-50%);
    color: ${theme.colors.grey1};
    font-size: 12px;
  }
`;

const ReportTypeDropdownStyles = createGlobalStyle`
  .report-type-dropdown.ant-select-dropdown {
    padding: 4px 0;
    border-radius: 8px;
    border: 1px solid ${theme.colors.grey2};
    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
    overflow: hidden;
  }

  .report-type-dropdown .ant-select-item {
    padding: 8px 12px;
    border-radius: 0;
    font-size: 14px;
    line-height: 20px;
    color: #111827;
    background: #fff !important;
  }

  .report-type-dropdown .ant-select-item-option-selected {
    background: #e6f4ff !important;
    font-weight: 700;
    color: #111827;
  }

  .report-type-dropdown .ant-select-item-option-active:not(.ant-select-item-option-selected) {
    background: #f5f5f5 !important;
  }

  .report-type-dropdown .ant-select-item-option-state {
    display: none;
  }
`;

const SectionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-width: 0;
`;

const KpiNote = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.grey1};
`;

const CardWrapper = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  ${tableScrollCss}
`;

const SearchWrapper = styled.div`
  ${searchFieldCss}
  display: flex;
  background-color: ${theme.colors.grey4};
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  padding: 0 8px;
  align-items: center;
  box-sizing: border-box;
  gap: 8px;
  flex: none;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const CustomSearch = styled(Input)`
  border-radius: 12px;
  height: 40px;
  font-size: 16px;
  border: none;
  box-shadow: none;
  background-color: ${theme.colors.grey4};
  padding: 0;

  &:hover,
  &:focus {
    border: none;
    box-shadow: none;
    background-color: ${theme.colors.grey4};
  }
`;

const NameLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  text-align: left;
  font-family: inherit;
  color: #1890ff;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  cursor: pointer;
`;

const MetricCell = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #000;
`;

const CreatedTime = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #000;
  white-space: nowrap;
`;

const ChannelCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #1f1f1f;
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: white;
    border-bottom: none;
    padding: 10px 16px;
    font-size: 14px;
    line-height: 20px;
    color: ${theme.colors.grey1};
    font-weight: 500;
  }

  .ant-table-thead > tr > th::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    border-bottom: none;
    height: 64px;
  }

  .ant-table-tbody > tr:nth-child(odd) {
    background-color: ${theme.colors.grey3};
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: ${theme.colors.grey4};
  }

  .ant-table table {
    border-spacing: 0 4px;
    border-collapse: separate;
  }

  .ant-table-tbody > tr > td:first-child {
    border-top-left-radius: 16px;
    border-bottom-left-radius: 16px;
  }

  .ant-table-tbody > tr > td:last-child {
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export default ReportPage;
